<script>
	import {
		Badge,
		DesktopNavigation,
		DesktopTabBar,
		Footer,
		Header,
		Icon,
		IconWrapper,
		LoadingSpinner,
		TabBar,
		UserAvatar
	} from '@resider/design-system';
	import { t } from '@resider/i18n';
	import { afterNavigate, goto, invalidate, pushState } from '$app/navigation';
	import { navigating, page } from '$app/stores';
	import { env as envPublic } from '$env/dynamic/public';
	import { onMount } from 'svelte';
	import scroll from '@resider/scroll';
	import handleUrlSearchParams from './handle-url-search-params.js';
	import switchSearchMode from '$lib/modules/explore/switch-search-mode';
	import toggleMap from '$lib/modules/explore/toggle-map';
	import { browser } from '$app/environment';
	import { activeFilterCount } from '$lib/modules/filters';

	export let data;

	let scrollY = 0;

	let fromChat = false;

	/** @type {number}*/
	let innerHeight;

	/** @type {number}*/
	let innerWidth;

	/** @type {HTMLDialogElement} */
	let loadingDialog;

	$: ({ session, supabase } = data);

	$: footerMenusList = $page.data.menusLists?.find(
		(/** @type {{ name: string; }} */ x) => x.name === `Footer Menus List [${$page.params.lang}]`
	).data.items;

	$: headerDesktopWidth =
		$page.url.pathname === `/${$page.params.lang}` ? '86rem' : $page.params.chat ? '68rem' : '100%';

	onMount(() => {
		const { data } = supabase.auth.onAuthStateChange((_, newSession) => {
			if (newSession?.expires_at !== session?.expires_at) {
				invalidate('supabase:auth');
			}
		});

		return () => data.subscription.unsubscribe();
	});

	afterNavigate(({ from, to }) => {
		if (to?.url) {
			handleUrlSearchParams(to.url);
		}

		if (from?.params?.chat) {
			fromChat = true;
		} else {
			fromChat = false;
		}
	});

	$: if (loadingDialog && $navigating !== null) {
		loadingDialog.showModal();
	} else if (loadingDialog) {
		loadingDialog.close();
	}

	$: $scroll.scrollY = scrollY;

	$: if (innerHeight) {
		document.body.style.setProperty('--innerHeight', `${innerHeight}px`);
	}
</script>

<svelte:window
	bind:innerWidth
	bind:innerHeight
	bind:scrollY
	on:scroll={() => scroll.detectScrollDirection()}
/>

{#if import.meta.env.PROD && $page.url.origin.includes('resider.pl')}
	<script type="text/javascript">
		(function (window, document, dataLayerName, id) {
			(window[dataLayerName] = window[dataLayerName] || []),
				window[dataLayerName].push({ start: new Date().getTime(), event: 'stg.start' });
			var scripts = document.getElementsByTagName('script')[0],
				tags = document.createElement('script');
			function stgCreateCookie(a, b, c) {
				var d = '';
				if (c) {
					var e = new Date();
					e.setTime(e.getTime() + 24 * c * 60 * 60 * 1e3), (d = '; expires=' + e.toUTCString());
					f = '; SameSite=Strict';
				}
				document.cookie = a + '=' + b + d + f + '; path=/';
			}
			var isStgDebug =
				(window.location.href.match('stg_debug') || document.cookie.match('stg_debug')) &&
				!window.location.href.match('stg_disable_debug');
			stgCreateCookie('stg_debug', isStgDebug ? 1 : '', isStgDebug ? 14 : -1);
			var qP = [];
			dataLayerName !== 'dataLayer' && qP.push('data_layer_name=' + dataLayerName),
				isStgDebug && qP.push('stg_debug');
			var qPString = qP.length > 0 ? '?' + qP.join('&') : '';
			(tags.async = !0),
				(tags.src = 'https://resider.containers.piwik.pro/' + id + '.js' + qPString),
				scripts.parentNode.insertBefore(tags, scripts);
			!(function (a, n, i) {
				a[n] = a[n] || {};
				for (var c = 0; c < i.length; c++)
					!(function (i) {
						(a[n][i] = a[n][i] || {}),
							(a[n][i].api =
								a[n][i].api ||
								function () {
									var a = [].slice.call(arguments, 0);
									'string' == typeof a[0] &&
										window[dataLayerName].push({
											event: n + '.' + i + ':' + a[0],
											parameters: [].slice.call(arguments, 1)
										});
								});
					})(i[c]);
			})(window, 'ppms', ['tm', 'cm']);
		})(window, document, 'dataLayer', '969b917f-8b11-4348-8caf-ccd6ab79b86f');
	</script>
{/if}

{#if $page.status === 200 && !$page.data.isAuthPage && !(innerWidth < 768 && $page.params.offer) && !$page.params.marketing_consents}
	<Header
		scrollDirection={$page.params.explore &&
		$page.state.exploreMapIsVisible &&
		browser &&
		window.location.search.includes('show_map=true') // ||
			? // $page.params.chat
				'up'
			: $scroll.direction}
		desktopWidth={headerDesktopWidth}
		hasGradientBackground={$page.state.exploreMapIsVisible ||
			(browser && window.location.search.includes('show_map=true'))}
	>
		<svelte:fragment slot="left">
			{#if ($page.params.explore && $page.url.searchParams.has('chat_id') && fromChat) || ($page.params.account && !$page.url.pathname.endsWith($t('slugs.account')))}
				<Icon
					name="arrow-left"
					on:click={() => {
						if ($page.params.account) {
							goto(`/${$page.params.lang}/${$t('slugs.account')}`);
						} else if ($page.url.searchParams.has('chat_id') && fromChat) {
							goto(
								`/${$page.params.lang}/${$t('slugs.chat')}/${$page.url.searchParams.get('chat_id')}`
							);
						}
					}}
				/>
			{:else if $page.params.explore}
				<Icon
					name="filters"
					on:click={() =>
						switchSearchMode({
							target: {
								value: window.location.search.includes('show_filters=true') ? 'chat' : 'filters'
							}
						})}
				/>
				{#if $activeFilterCount > 0}
					<Badge size="xs">{$activeFilterCount}</Badge>
				{/if}
			{:else if $page.params.chat}
				<Icon
					name="list"
					on:click={() => {
						const newUrl = new URL(window.location.href);
						newUrl.searchParams.set('show_chat_navigation', 'true');

						pushState(newUrl.toString(), { ...$page.state, chatNavigationDialogIsOpen: true });
					}}
				/>
			{:else if $page.url.pathname === `/${$page.params.lang}` || $page.params.slug || $page.url.pathname.includes('blog')}
				<Icon
					name="language"
					on:click={() => {
						const newUrl = new URL(window.location.href);
						newUrl.searchParams.set('show_language_menu', 'true');

						pushState(newUrl.toString(), { ...$page.state, languageMenuIsOpen: true });
					}}
				/>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="right">
			{#if $page.params.explore}
				{#if $page.state.exploreMapIsVisible || (browser && window.location.search.includes('show_map=true'))}
					<Icon
						name="listing"
						on:click={() =>
							toggleMap(window.location.href, window.location.search.includes('show_map=true'))}
					/>
				{:else}
					<Icon
						name="map"
						on:click={() =>
							toggleMap(window.location.href, window.location.search.includes('show_map=true'))}
					/>
				{/if}
			{:else if $page.params.chat}
				<Icon
					name="create-new"
					on:click={() => {
						if ($page.data.user?.id) {
							goto(`/${$page.params.lang}/${$t('slugs.chat')}?new_chat=true`, {
								invalidateAll: true
							});
						} else {
							const newUrl = new URL(window.location.href);
							newUrl.searchParams.set('show_chat_navigation', 'true');

							pushState(newUrl.toString(), { ...$page.state, chatNavigationDialogIsOpen: true });
						}
					}}
				/>
			{:else if $page.url.pathname === `/${$page.params.lang}` || $page.params.slug || $page.url.pathname.includes('blog')}
				<Icon
					name="menu"
					on:click={() => {
						const newUrl = new URL(window.location.href);
						newUrl.searchParams.set('show_mobile_menu', 'true');

						pushState(newUrl.toString(), { ...$page.state, mobileMenuIsOpen: true });
					}}
				/>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="centerDesktop">
			{#if !$page.params.account}
				<DesktopTabBar let:MenuItem>
					{#if $page.data.headerMenuItems?.length}
						{#each $page.data.headerMenuItems as item}
							<MenuItem
								size="sm"
								href={item.href.Default}
								kind={item.active || $page.url?.pathname.includes(item.href.Default)
									? 'tertiary'
									: 'ghost-alt'}
							>
								<Icon name={item.iconName} />

								{item.label.Default}

								{#if item.label.Default === $t('global.Liked')}
									<Badge kind="secondary" size="xs"
										>{$page.data?.userOffers?.liked?.length ?? 0}</Badge
									>
								{/if}
							</MenuItem>
						{/each}
					{/if}
				</DesktopTabBar>
			{/if}
		</svelte:fragment>

		<DesktopNavigation
			userName={$page.data?.user?.user_metadata?.name ?? $page.data?.user?.email}
			userPicture={$page.data?.user?.user_metadata.avatar_url}
			slot="rightDesktop"
		>
			<svelte:fragment slot="menu" let:Button let:handleClickMenuItem>
				{#if !$page.data.user}
					<div class="auth-ctas">
						<Button
							href="/{$page.params.lang}/{$t('slugs.sign-up')}?next={encodeURIComponent(
								$page.url.href
							)}"
						>
							{$t('global.Join for free')}
						</Button>

						<Button
							href="/{$page.params.lang}/{$t('slugs.sign-in')}?next={encodeURIComponent(
								$page.url.href
							)}"
							kind="tertiary"
						>
							{$t('global.Sign in')}
						</Button>
					</div>
				{:else}
					<li>
						<Button
							href="/{$page.params.lang}/{$t('slugs.account')}"
							on:click={handleClickMenuItem}
							kind={$page.url.pathname.includes('konto') ? 'tertiary' : 'ghost-alt'}
							size="sm"
						>
							<IconWrapper>
								<Icon name="user" />
							</IconWrapper>
							{$t('global.Account')}
						</Button>
					</li>
				{/if}
				{#if $page.data.navigationMenuItems?.length}
					{#each $page.data.navigationMenuItems as item}
						<li>
							<Button
								href={item.href.Default}
								on:click={handleClickMenuItem}
								kind={item.href.Default.includes($page.url?.pathname) &&
								!$page.url.pathname.match(/^\/pl$|^\/en$|^\/uk$/) &&
								$page.url.pathname !== '/'
									? 'tertiary'
									: 'ghost-alt'}
								size="sm"
								--icon-wrapper-padding="0.5rem"
							>
								<IconWrapper>
									<Icon name={item.iconName} />
								</IconWrapper>
								{item.label.Default}
							</Button>
						</li>
					{/each}
				{/if}
			</svelte:fragment>

			<svelte:fragment slot="languages" let:Button let:handleClickMenuItem>
				<Button
					href="/pl"
					size="sm"
					kind={$page.params.lang === 'pl' ? 'tertiary' : 'ghost-alt'}
					reload
					on:click={handleClickMenuItem}
				>
					🇵🇱 Polski
				</Button>

				<Button
					href="/en"
					size="sm"
					kind={$page.params.lang === 'en' ? 'tertiary' : 'ghost-alt'}
					reload
					on:click={handleClickMenuItem}
				>
					🇬🇧 English
				</Button>
			</svelte:fragment>
		</DesktopNavigation>
	</Header>
{/if}

<slot />

{#if !$page.params.chat && !$page.params.explore && !$page.params.marketing_consents}
	<Footer
		facebookUrl={envPublic.PUBLIC_SOCIAL_URL_FACEBOOK}
		instagramUrl={envPublic.PUBLIC_SOCIAL_URL_INSTAGRAM}
		linkedinUrl={envPublic.PUBLIC_SOCIAL_URL_LINKEDIN}
		menusList={footerMenusList}
	/>
{/if}

{#if !$page.params.marketing_consents}
	<TabBar
		let:TabBarItem
		scrolled={$scroll.scrollY > 0 && innerWidth < 768}
		scrollDirection={!$page.url.searchParams.has('show_map') &&
		!$page.state.exploreMapIsVisible &&
		browser &&
		!window.location.search.includes('show_map=true')
			? $scroll.direction
			: 'up'}
	>
		<TabBarItem
			active={$page.url.pathname.match(/czat|chat/)}
			href="/{$page.params.lang}/{$t('slugs.chat')}{$page.data.userChats?.length > 0
				? `/${$page.data.userChats[0].id}`
				: ''}"
			icon="sparks"
			label={$t('global.AI Assistant')}
		/>

		<TabBarItem
			active={$page.url.pathname.match(/odkrywaj|explore/)}
			href="/{$page.params.lang}/{$t('slugs.explore')}"
			icon="search"
			label={$t('global.Explore')}
		/>

		<TabBarItem
			active={$page.url.pathname.match(/polubione|liked/)}
			badgeLabel={$page.data?.userOffers?.liked?.length > 0
				? $page.data?.userOffers?.liked?.length
				: '0'}
			href="/{$page.params.lang}/{$t('slugs.liked')}"
			icon="heart"
			label={$t('global.Liked')}
		/>

		<TabBarItem
			active={$page.url.pathname.match(/konto|account/)}
			href={$page.data?.user
				? `/${$page.params.lang}/${$t('slugs.account')}${
						!$page.data?.user ? `?next=${encodeURIComponent($page.url.href)}` : ''
					}`
				: `/${$page.params.lang}/${$t('slugs.sign-in')}${
						!$page.data?.user ? `?next=${encodeURIComponent($page.url.href)}` : ''
					}`}
			icon="user"
			label={$t('global.Account')}
		>
			<UserAvatar
				--size="1.5rem"
				slot="icon"
				userName={$page.data?.user?.user_metadata?.name ?? $page.data?.user?.email}
				userPicture={$page.data?.user?.user_metadata.avatar}
			/>
		</TabBarItem>
	</TabBar>
{/if}

<dialog class="loading-dialog" bind:this={loadingDialog}>
	<LoadingSpinner --size="3rem" />
</dialog>

<style>
	:global(.tab-bar) {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;

		@media (width > 767px) {
			display: none !important;
		}
	}

	.loading-dialog {
		border: none;
		background: none;
		transition: all var(--time-xs) ease-out;

		&::backdrop {
			background: hsla(var(--hsl-white), 0.25);
			backdrop-filter: blur(4px);
		}
	}
</style>
